import * as Sentry from '@sentry/vue';
import { createPinia } from 'pinia';
import { createApp } from 'vue';

import { sentryBaseConfig } from '~/config/sentry';

import { useServiceContainer, useStoreContainer } from '~/container';

import EtAction from '~/modules/core/components/EtAction.vue';
import EtAgnosticRouterLink from '~/modules/core/components/EtAgnosticRouterLink.vue';
import EtCookieBanner from '~/modules/core/components/EtCookieBanner.vue';
import EtCurrencySelectorButton from '~/modules/core/components/EtCurrencySelectorButton.vue';
import EtCustomerRecoveryUnsubscribeForm from '~/modules/core/components/EtCustomerRecoveryUnsubscribeForm.vue';
import EtExpandablePanel from '~/modules/core/components/EtExpandablePanel.vue';
import EtFlag from '~/modules/core/components/EtFlag.vue';
import EtFooterAccordion from '~/modules/core/components/EtFooterAccordion.vue';
import EtHappyBirthdayUnsubscribeForm from '~/modules/core/components/EtHappyBirthdayUnsubscribeForm.vue';
import EtHorizontalCarousel from '~/modules/core/components/EtHorizontalCarousel.vue';
import EtIcon from '~/modules/core/components/EtIcon.vue';
import EtLoadingSpinnerCircular from '~/modules/core/components/EtLoadingSpinnerCircular.vue';
import EtLocaleSelector from '~/modules/core/components/EtLocaleSelector.vue';
import EtMenuButton from '~/modules/core/components/EtMenuButton.vue';
import EtNavigation from '~/modules/core/components/EtNavigation.vue';
import EtNewsletterSubscribeModalButton from '~/modules/core/components/EtNewsletterSubscribeModalButton.vue';
import EtNewsletterUnsubscribeForm from '~/modules/core/components/EtNewsletterUnsubscribeForm.vue';
import EtPrice from '~/modules/core/components/EtPrice.vue';
import EtQuoteUnsubscribeForm from '~/modules/core/components/EtQuoteUnsubscribeForm.vue';
import EtRating from '~/modules/core/components/EtRating.vue';
import EtRoundedWorryFreeLabel from '~/modules/core/components/EtRoundedWorryFreeLabel.vue';
import EtSimpleDataSelector from '~/modules/core/components/EtSimpleDataSelector.vue';
import EtStateResolve from '~/modules/core/components/EtStateResolve.vue';
import EtStickySearchHeader from '~/modules/core/components/EtStickySearchHeader.vue';
import EtSupplierLogo from '~/modules/core/components/EtSupplierLogo.vue';
import EtSupplierRatingScore from '~/modules/core/components/EtSupplierRatingScore.vue';
import EtWorryFreeModalButton from '~/modules/core/components/EtWorryFreeModalButton.vue';
import EtModalContainer from '~/modules/core/components/modal/EtModalContainer.vue';
import EtOverlayContainer from '~/modules/core/components/overlay/EtOverlayContainer.vue';
import EtTooltipContainer from '~/modules/core/components/tooltip/EtTooltipContainer.vue';
import EtArticleBarChart from '~/modules/landing/components/EtArticleBarChart.vue';
import EtArticleDecisionTree from '~/modules/landing/components/EtArticleDecisionTree.vue';
import EtCarClassRateShowMoreTileButton from '~/modules/landing/components/EtCarClassRateShowMoreTileButton.vue';
import EtCarClassRateTileButton from '~/modules/landing/components/EtCarClassRateTileButton.vue';
import EtCollapse from '~/modules/landing/components/EtCollapse.vue';
import EtDestinationCarClassRatesChart from '~/modules/landing/components/EtDestinationCarClassRatesChart.vue';
import EtDestinationsMap from '~/modules/landing/components/EtDestinationsMap.vue';
import EtGuideWithHeroSearchButton from '~/modules/landing/components/EtGuideWithHeroSearchButton.vue';
import EtPopularLocationDestinationsTilesButton from '~/modules/landing/components/EtPopularLocationDestinationsTilesButton.vue';
import EtSearchModalButton from '~/modules/landing/components/EtSearchModalButton.vue';
import EtSectionLead from '~/modules/landing/components/EtSectionLead.vue';
import EtStickyPageIndexHeader from '~/modules/landing/components/EtStickyPageIndexHeader.vue';
import EtSupplierReviewsWithLoadMoreButton from '~/modules/landing/components/EtSupplierReviewsWithLoadMoreButton.vue';
import EtSupplierSummarySectionButton from '~/modules/landing/components/EtSupplierSummarySectionButton.vue';
import EtSupplierTileDetailsButton from '~/modules/landing/components/EtSupplierTileDetailsButton.vue';
import EtSupplierTileSearchButton from '~/modules/landing/components/EtSupplierTileSearchButton.vue';
import EtUserPreloader from '~/modules/landing/components/EtUserPreloader.vue';
import EtWorryFreeBlock from '~/modules/landing/components/EtWorryFreeBlock.vue';
import EtMainSearchOverlayBtn from '~/modules/searchQuery/components/EtMainSearchOverlayBtn.vue';
import EtSearchMainState from '~/modules/searchQuery/components/EtSearchMainState.vue';
import EtForgotPasswordLink from '~/modules/user/components/EtForgotPasswordLink.vue';
import EtLoginBlock from '~/modules/user/components/EtLoginBlock.vue';
import EtUserLoginButton from '~/modules/user/components/EtUserLoginButton.vue';
import EtUserLogoutButton from '~/modules/user/components/EtUserLogoutButton.vue';
import EtUserMenu from '~/modules/user/components/EtUserMenu.vue';
import EtUserMenuButton from '~/modules/user/components/EtUserMenuButton.vue';
import EtUserRegisterButton from '~/modules/user/components/EtUserRegisterButton.vue';

import App from './App';
import deepAssign from '../../../lib/deep-assign';
import immutableUpdate from '../../../lib/immutable-update';
import structuredCloneAlternative from '../../../lib/structured-clone-alternative';

import loadVueTemplates from '~/globals/loadVueTemplates';
import {
    ClickOutside,
    DynamicText,
    ExitIntent,
    MapFocusDestination,
    OnIntersection,
    OnSrcError,
    WindowFocus,
} from '~/modules/core/directives';
import { buildErrorData } from '~/modules/core/errors';
import EtDropdown from '~/modules/core/EtDropdown.vue';
import { CustomerSupportModalHandler } from '~/modules/customerService/directives';
import { FocusFieldEvent, FocusFieldTrigger, ScrollTopElement } from '~/modules/form/directives';

window.deepClone = structuredCloneAlternative;
window.deepAssign = deepAssign;
window.immutableUpdate = immutableUpdate;

const components = {
    'et-state-resolve': EtStateResolve,
    'et-search-main-state': EtSearchMainState,
    'et-tooltip-container': EtTooltipContainer,
    'et-modal-container': EtModalContainer,
    'et-overlay-container': EtOverlayContainer,
    'et-flag': EtFlag,
    'et-price': EtPrice,
    'et-supplier-logo': EtSupplierLogo,
    'et-dropdown': EtDropdown,
    'et-rating': EtRating,
    'et-rounded-worry-free-label': EtRoundedWorryFreeLabel,
    'et-worry-free-modal-button': EtWorryFreeModalButton,
    'et-sticky-search-header': EtStickySearchHeader,
    'et-search-modal-button': EtSearchModalButton,
    'et-expandable-panel': EtExpandablePanel,
    'et-user-login-button': EtUserLoginButton,
    'et-currency-selector-button': EtCurrencySelectorButton,
    'et-locale-selector': EtLocaleSelector,
    'et-user-menu-button': EtUserMenuButton,
    'et-user-menu': EtUserMenu,
    'et-user-register-button': EtUserRegisterButton,
    'et-forgot-password-link': EtForgotPasswordLink,
    'et-newsletter-subscribe-modal-button': EtNewsletterSubscribeModalButton,
    'et-user-logout-button': EtUserLogoutButton,
    'et-main-search-overlay-btn': EtMainSearchOverlayBtn,
    'et-menu-button': EtMenuButton,
    'et-footer-accordion': EtFooterAccordion,
    'et-cookie-banner': EtCookieBanner,
    'et-icon': EtIcon,
    'et-agnostic-router-link': EtAgnosticRouterLink,
    'et-sticky-page-index-header': EtStickyPageIndexHeader,
    'et-simple-data-selector': EtSimpleDataSelector,
    'et-section-lead': EtSectionLead,
    'et-horizontal-carousel': EtHorizontalCarousel,
    'et-worry-free-block': EtWorryFreeBlock,
    'et-car-class-rate-tile-button': EtCarClassRateTileButton,
    'et-car-class-rate-show-more-tile-button': EtCarClassRateShowMoreTileButton,
    'et-collapse': EtCollapse,
    'et-popular-location-destinations-tiles-button': EtPopularLocationDestinationsTilesButton,
    'et-supplier-tile-details-button': EtSupplierTileDetailsButton,
    'et-destination-car-class-rates-chart': EtDestinationCarClassRatesChart,
    'et-supplier-tile-search-button': EtSupplierTileSearchButton,
    'et-destinations-map': EtDestinationsMap,
    'et-guide-with-hero-search-button': EtGuideWithHeroSearchButton,
    'et-supplier-reviews-with-load-more-button': EtSupplierReviewsWithLoadMoreButton,
    'et-supplier-rating-score': EtSupplierRatingScore,
    'et-login-block': EtLoginBlock,
    'et-user-preloader': EtUserPreloader,
    'et-supplier-summary-section-button': EtSupplierSummarySectionButton,
    'et-loading-spinner-circular': EtLoadingSpinnerCircular,
    'et-newsletter-unsubscribe-form': EtNewsletterUnsubscribeForm,
    'et-quote-unsubscribe-form': EtQuoteUnsubscribeForm,
    'et-customer-recovery-unsubscribe-form': EtCustomerRecoveryUnsubscribeForm,
    'et-happy-birthday-unsubscribe-form': EtHappyBirthdayUnsubscribeForm,
    'et-navigation': EtNavigation,
    'et-action': EtAction,
    'et-article-bar-chart': EtArticleBarChart,
    'et-article-decision-tree': EtArticleDecisionTree,
};

const app = createApp(App);

// Register all needed components for landing pages
Object.entries(components).forEach(([componentName, component]) => {
    app.component(componentName, component);
});

app.use(createPinia());

useStoreContainer().AppStore.setAppInstance(app);

app.directive('scrollTopElement', ScrollTopElement);
app.directive('focusFieldTrigger', FocusFieldTrigger);
app.directive('focusFieldEvent', FocusFieldEvent);
app.directive('onSrcError', OnSrcError);
app.directive('dynamicText', DynamicText);
app.directive('customerSupportModalHandler', CustomerSupportModalHandler);
app.directive('exitIntent', ExitIntent);
app.directive('windowFocus', WindowFocus);
app.directive('onIntersection', OnIntersection);
app.directive('mapFocusDestination', MapFocusDestination);
app.directive('clickOutside', ClickOutside);

// Doesn't work if adding to vite config
app.config.compilerOptions.isCustomElement = (tag) => ['vue-template', 'vr'].includes(tag);

app.config.errorHandler = (error, _instance, info) => {
    useServiceContainer().BackendLoggerService.log(
        'error',
        'Uncaught error occurred in Vue',
        buildErrorData(error, { origin: 'landing', info }),
    );
};

window.onerror = (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
    useServiceContainer().BackendLoggerService.log(
        'error',
        'Uncaught error occurred',
        buildErrorData(error ?? event, { origin: 'landing', source, lineno, colno }),
    );
};

Sentry.init({
    ...sentryBaseConfig,
    app,
    integrations: [Sentry.browserTracingIntegration()],
});

loadVueTemplates('landing').then(() => {
    app.mount('#app');
});
